import "./landing.css";
import { useMemo } from "react";
import { Helmet } from "react-helmet";

import { HeaderLanding } from "../../components/landing/HeaderLanding/HeaderLanding";
import { FooterLanding } from "../../components/landing/FooterLanding/FooterLanding";
import { ScrollTop } from "../../components/landing/ScrollTop/ScrollTop";
import { useHomeContent } from "./hooks/useHomeContent";
import { useLandingLanguages } from "./hooks/useLandingLanguages";

import { Hero } from "./sections/Hero/Hero";
import { Reviews } from "./sections/Reviews/Reviews";
import { Offer } from "./sections/Offer/Offer";
import { Lead } from "./sections/Lead/Lead";
import { AdvantagesAbout } from "./sections/AdvantagesAbout/AdvantagesAbout";
import { Clients } from "./sections/Clients/Clients";
import { Promo } from "./sections/Promo/Promo";
import { Awards } from "./sections/Awards/Awards";
import { Consultation } from "./sections/Consultation/Consultation";

const toHtmlLang = (code: string) => (code === "ua" ? "uk" : code);

const toOgLocale = (code: string) => {
  const normalized = String(code || "ua").toLowerCase();
  if (normalized === "ua" || normalized === "uk") return "uk_UA";
  if (normalized === "en") return "en_US";
  return `${normalized}_${normalized.toUpperCase()}`;
};

const buildLangUrl = (code: string) => {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  url.searchParams.set("lang", code);
  url.hash = "";
  return url.toString();
};

export const LandingPage = () => {
  const data = useHomeContent();
  const { currentCode, availableCodes } = useLandingLanguages();

  const heroTitle = [data?.hero?.titlePrefix, data?.hero?.titleAccent]
    .filter(Boolean)
    .join(" ")
    .trim();

  const seoTitle =
    data?.seo?.title ||
    (heroTitle ? `${heroTitle} | Office Centre` : "Office Centre B2B");

  const seoDescription =
    data?.seo?.description ||
    data?.hero?.subtitle ||
    data?.promo?.description ||
    "B2B solutions and services by Office Centre.";

  const canonicalUrl = useMemo(
    () => buildLangUrl(currentCode || "ua"),
    [currentCode],
  );

  const xDefaultCode = availableCodes.includes("ua")
    ? "ua"
    : availableCodes[0] || "ua";
  const xDefaultUrl = useMemo(() => buildLangUrl(xDefaultCode), [xDefaultCode]);

  return (
    <div className="oc-page">
      <Helmet htmlAttributes={{ lang: toHtmlLang(currentCode || "ua") }}>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />

        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:locale" content={toOgLocale(currentCode || "ua")} />
        {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />

        {availableCodes.map((code) => {
          const href = buildLangUrl(code);
          if (!href) return null;
          return (
            <link
              key={`alt-${code}`}
              rel="alternate"
              hrefLang={toHtmlLang(code)}
              href={href}
            />
          );
        })}

        {xDefaultUrl ? <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} /> : null}
      </Helmet>

      <HeaderLanding />
      <main>
        <Hero />
        <Reviews />
        <Offer />
        <Consultation />
        <AdvantagesAbout />
        <Clients />
        <Promo />
        <Awards />
        <Lead />
        <ScrollTop />
      </main>
      <FooterLanding />
    </div>
  );
};
